<template>
  <div class="row">
    <card-default>
      <div
        slot="title"
        class="card-heading">
        <div class="row">
          <div class="col h5">
            {{ newComponent.uuid ? 'Edit' : 'Create' }} component
          </div>
          <div class="col float-right">
            <auth-button
              :permission="newComponent.uuid ? 'component-update' : 'component-create'"
              class="btn btn-primary btn-micro float-right"
              @click="proxySave"
            >
              Save
            </auth-button>
          </div>
        </div>
        <hr>
      </div>
      <div
        slot="body"
        class="card-text">
        <div class="row">
          <field-input
            v-model="newComponent.name"
            name="name"
            label="Name"
            class="col-4"
          />
          <field-select2
            v-model="newComponent.type"
            :options="componentTypes"
            :on-change="type => newComponent.type = type"
            name="type"
            label="Type"
            class="col-4"
          />
          <field-select2
            v-model="newComponent.language"
            :options="allLanguages"
            :on-change="setLanguage"
            clearable
            name="language"
            label="Language"
            class="col-4"
          />
        </div>
        <div class="row">
          <div
            v-for="(subComponentLevel, level) in subComponentLevels"
            :key="level"
            class="col"
            style="border-right: 1px solid blue"
          >
            <div
              v-for="(subComponent, index) in subComponentLevel.subComponents"
              :key="index"
            >
              <div
                :class="{ 'background-blue': level === 0 || selectedComponentByLevel[level] === subComponent }"
                class="pointer mb-2"
              >
                <div
                  class="ml-1"
                  @click="selectSubComponent(subComponent, level)"
                >
                  <h5>{{ subComponent.name }}</h5>
                  <h6 v-if="level === 0">{{ subComponent.json.path }}</h6>
                  <h6 v-if="level !== 0">{{ subComponent.path }}</h6>
                </div>
                <div class="ml-1">
                  <i
                    class="fa fa-pencil"
                    title="Edit"
                    @click="editSubComponent(subComponent, level)"
                  />
                  &nbsp;
                  <i
                    class="fa fa-remove"
                    title="Delete"
                    @click="deleteSubComponent(subComponentLevel.parent, subComponent)"
                  />
                </div>
              </div>
            </div>
            <button
              v-if="level !== 0"
              class="btn btn-primary btn-micro"
              @click="addSubComponent(subComponentLevel.parent, level)"
            >
              <i class="fa fa-plus"/> add
            </button>
          </div>
        </div>
      </div>
    </card-default>
    <div v-if="showAddingModal">
      <modal :close="closeAddingModal">
        <div slot="header">
          {{ editingSubComponent ? 'Edit' : 'Add' }} component
        </div>
        <div slot="body">
          <div class="row">
            <field-select2
              v-if="editingLevel === 0"
              v-model="selectedBaseComponent"
              :options="preDefinedComponentNames"
              :on-change="selectComponent"
              name="component"
              label="Component"
              class="col-12"
            />
          </div>
          <div
            v-if="editingLevel !== 0"
            class="row"
          >
            <field-select2
              v-model="newSubComponent.name"
              :options="subComponentList"
              :on-change="selectComponent"
              class="col-12"
              label="Component"
              name="subComponent"
              searchable
            />
          </div>
          <div
            v-for="(field, index) in newSubComponent.fields"
            :key="index"
            class="row"
          >
            <div class="form-group col-12">
              <div class="input-group">
                <label class="control-label">
                  {{ field.label }}
                </label>
                <input
                  v-if="field.type === 'string'"
                  v-model="newSubComponent.config[field.key]"
                  type="text"
                >
                <field-rich-text
                  v-if="field.type === 'wysiwyg'"
                  v-model="newSubComponent.config.content"
                  @input="newSubComponent.config.content = $event"
                />
                <field-json
                  v-if="field.type === 'json'"
                  v-model="newSubComponent.configString"
                />
              </div>
            </div>
          </div>
        </div>
        <div slot="footer">
          <button
            :disabled="!componentSelected"
            class="btn btn-primary"
            @click="saveSubComponent">
            Save
          </button>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import CardDefault from '@/components/CardDefault'
import FieldInput from '@/components/form/FieldInput'
import FieldSelect2 from '@/components/form/FieldSelect2'
import FieldRichText from '@/components/form/FieldRichText'
import Vue from 'vue'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { js_beautify as jsBeautify } from 'js-beautify'
import preDefinedComponents from '../preDefinedComponents'
import Modal from '@/components/Modal'
import FieldJson from '@/components/form/FieldJson'

export default {
  name: 'ComponentCreate',
  components: {
    FieldInput,
    FieldSelect2,
    CardDefault,
    FieldRichText,
    Modal,
    FieldJson
  },
  data () {
    return {
      selectedComponentByLevel: [],
      componentTypes: [
        'Component',
        'Content',
        'Layout'
      ],
      showAddingModal: false,
      editingSubComponent: false,
      preDefinedComponents: preDefinedComponents,
      componentSelected: false,
      newSubComponent: {
        name: '',
        path: '',
        config: {
          content: ''
        },
        configString: '',
        fields: [],
        components: []
      }
    }
  },
  computed: {
    ...mapGetters({
      newComponent: 'component/newComponent',
      componentNameArray: 'component/componentNameArray',
      allLanguages: 'language/languagesSimple'
    }),
    subComponentList () {
      let nameArray = this.preDefinedComponentNames
      let otherComponents = this.componentNameArray.filter(componentName => {
        return componentName !== this.newComponent.name
      })
      return nameArray.concat(otherComponents)
    },
    subComponentLevels () {
      let preDefinedComponentIndex = {}
      this.preDefinedComponents.forEach(component => {
        preDefinedComponentIndex[component.name] = true
      })

      let levels = [{
        subComponents: [this.newComponent]
      }, {
        parent: this.newComponent.json,
        subComponents: []
      }]
      let addComponentsToLevel = (level) => {
        if (levels[level].parent.components) {
          levels[level].parent.components.forEach(subComponent => {
            levels[level].subComponents.push(subComponent)
            if (
              preDefinedComponentIndex[subComponent.name] &&
              this.selectedComponentByLevel[level] === subComponent
            ) {
              levels[level + 1] = {
                parent: subComponent,
                subComponents: []
              }
              addComponentsToLevel(level + 1)
            }
          })
        }
      }
      addComponentsToLevel(1)
      return levels
    },
    preDefinedComponentNames () {
      let nameArray = []
      this.preDefinedComponents.forEach(component => {
        nameArray.push(component.name)
      })
      return nameArray
    },
    selectedBaseComponent () {
      if (this.newComponent.json.baseComponent) {
        return this.newComponent.json.baseComponent
      }
      return 'JSON'
    }
  },
  created () {
    this.getLanguages().then(() => {
      this.allLanguages.forEach(language => {
        if (language.value === this.newComponent.language_id) {
          this.setLanguage(language)
        }
      })
    })
  },
  methods: {
    ...mapActions({
      getLanguages: 'language/get',
      save: 'component/save'
    }),
    ...mapMutations({
      setNewProp: 'component/SET_NEW_PROP'
    }),
    addSubComponent (parent, level) {
      this.addingParent = parent
      this.editingSubComponent = false
      this.editingLevel = level
      this.showAddingModal = true
    },
    selectSubComponent (subComponent, level) {
      Vue.set(this.selectedComponentByLevel, level, subComponent)
    },
    deleteSubComponent (parent, subComponent) {
      if (confirm('Are you sure you want to delete this component?')) {
        parent.components.splice(parent.components.indexOf(subComponent), 1)
      }
    },
    closeAddingModal () {
      this.showAddingModal = false
      this.componentSelected = false
      this.resetNewSubComponent()
    },
    selectComponent (option) {
      if (this.editingLevel === 0) {
        if (option === 'JSON') {
          delete this.newComponent.json.baseComponent
        } else {
          this.newComponent.json.baseComponent = option
        }
      } else {
        this.newSubComponent.name = option
      }
      this.preDefinedComponents.forEach(component => {
        if (component.name === option) {
          if (option !== 'JSON') {
            if (this.editingLevel === 0) {
              this.newComponent.json.path = component.path
            } else {
              this.newSubComponent.path = component.path
            }
          }
          this.newSubComponent.fields = component.fields
          this.newSubComponent.fields.forEach(field => {
            if (field.type === 'json') {
              this.newSubComponent.configString = jsBeautify(
                JSON.stringify(this.newSubComponent.config),
                {
                  indent_size: 2
                }
              )
            }
          })
        }
      })
      this.componentSelected = true
    },
    editSubComponent (subComponent, level) {
      this.addingParent = null
      this.editingSubComponent = true
      this.editingLevel = level
      this.showAddingModal = true
      this.newSubComponent = subComponent
      if (level === 0) {
        if (!this.newComponent.config) {
          this.newComponent.config = this.newComponent.json.config
        }
        this.selectComponent(this.selectedBaseComponent)
      } else {
        this.selectComponent(subComponent.name)
      }
    },
    resetNewSubComponent () {
      this.newSubComponent = {
        name: '',
        path: '',
        config: {
          content: ''
        },
        configString: '',
        fields: [],
        components: []
      }
    },
    saveSubComponent () {
      let error = false
      this.newSubComponent.fields.forEach(field => {
        if (field.type === 'json') {
          try {
            this.newSubComponent.config = JSON.parse(this.newSubComponent.configString)
          } catch (e) {
            Vue.toasted.showError('Invalid JSON!')
            error = true
          }
        }
      })
      if (error) {
        return
      }
      if (!this.editingSubComponent) {
        this.newSubComponent.components = []
        if (!this.addingParent.components) {
          Vue.set(this.addingParent, 'components', [])
        }
        Vue.set(this.addingParent.components, this.addingParent.components.length, this.newSubComponent)
      }
      this.closeAddingModal()
    },
    proxySave () {
      if (this.newComponent.json.baseComponent) {
        this.newComponent.json.config = this.newComponent.config
        delete this.newComponent.config

        let preDefinedComponentIndex = {}
        this.preDefinedComponents.forEach(component => {
          preDefinedComponentIndex[component.name] = true
        })

        let clean = (component) => {
          delete component.fields
          delete component.configString
          if (component.config && component.config.length === 0) {
            delete component.config
          }
          if (component.components) {
            if (component.components.length === 0) {
              delete component.components
            } else {
              component.components.forEach((subComponent, index) => {
                if (!preDefinedComponentIndex[subComponent.name]) {
                  component.components[index] = subComponent.name
                } else {
                  clean(subComponent)
                }
              })
            }
          }
        }
        clean(this.newComponent)
        clean(this.newComponent.json)
      } else {
        this.newComponent.json = this.newComponent.config
        delete this.newComponent.config
      }

      this.save().then(() => {
        if (!this.newComponent.uuid) {
          this.$router.push('.')
        }
      })
    },
    setLanguage (language) {
      this.setNewProp({ prop: 'language', value: language })
    }
  }
}
</script>

<style>
  .background-blue {
    background-color: #eef;
  }
</style>
